import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Table, TableProps } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons';
import { useHistory } from 'umi';
import { getColumns } from './utils/common';
import { getNumberOfDotUnit } from '@gp/utils/common';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import GeneralModal from '@gp/components/GeneralModal';
import ProjectUpdateCard from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/components/ProjectUpdateCard';
import { formatGRActionItemsRes } from '@gp/pages/Pipeline/ProjectDetails/ProjectUpdate/utils';
// @ts-ignore
import PPSCommentDetails from '@gp/pages/Pipeline/Project/components/ProjectList/components/PPSCommentDetails';
import useReviewOrEditModal from '@gp/hooks/useReviewOrEditModal';
import classNames from 'classnames';
import './index.scss';

const { autotip, sortable, pageable, scrollable } = Table.addons;

interface OverviewTablePropsType {
  // className?: string;
  // style?: React.CSSProperties;
  data: any;
  loading: boolean;
  setSortsParams: React.Dispatch<React.SetStateAction<SortBy<any>>> | null;
}

const DEFAULT_PROPS: OverviewTablePropsType = {
  data: null,
  loading: true,
  setSortsParams: null,
};

function OverviewTable(props: OverviewTablePropsType) {
  const { data, loading: propsLoading, setSortsParams } = props;

  const history = useHistory();
  const [projectUpdatesVisible, setProjectUpdatesVisible] = useState(false);
  const [projectUpdatesModalList, setProjectUpdatesModalList] = useState(null);
  const projectUpdatesModalListNew = useMemo(() => {
    const { gr_action: grActions, ...restProjectUpdatesModalList } = (projectUpdatesModalList || {}) as any;
    return { ...restProjectUpdatesModalList, gr_action: formatGRActionItemsRes(grActions) };
  }, [projectUpdatesModalList]);
  const [records, setRecords] = useState([]);
  const [isClickSort, setIsClickSort] = useState<boolean>(false);
  const [sorts, setSorts] = useState<SortBy<any>>({
    by: 'priority',
    order: 'desc' as any,
  });
  const [paginationMsg, setPaginationMsg] = useState<{
    pageIndex: number;
    pageSize: number;
    total: number;
  }>({ pageIndex: 1, pageSize: 10, total: 0 });
  const [loading, setLoading] = useState<boolean>(true);
  // 表格是否出现纵向滚动条
  const [tableIsScroll, setTableIsScroll] = useState<boolean>(false);

  const [ppsViewable, setPPSViewable] = useState(false);
  const [updatesViewable, setUpdatesViewable] = useState<boolean>(false);
  const [salesViewable, setSalesViewable] = useState<boolean>(false);
  // pps comment 编辑、弹窗相关
  const {
    modalMode: ppsModalMode,
    modalTitle: ppsModalTitle,
    modalVisible: ppsModalVisible,
    modalData: ppsModalData,
    setModalData: ppsSetModalData,
    openModal: ppsOpenModal,
    closeModal: ppsCloseModal,
  } = useReviewOrEditModal({
    reviewModalTitle: 'DPS Comment',
    editModalTitle: 'DPS Comment Edit',
    defaultModalData: {
      list: null,
      view_pps: false,
    },
  });

  // 是否没权限
  const isNoPermission = useMemo(() => {
    return data?.edit === -1 && !loading;
  }, [data?.edit, loading]);

  // 虚假的loading，目的是为了让横纵滚动条回到初始位置
  const resetScroll = useCallback(() => {
    setLoading(true);
    const t = setTimeout(() => {
      clearTimeout(t);
      setLoading(false);
    }, 50);
  }, []);

  useEffect(() => {
    setLoading(propsLoading);
  }, [propsLoading]);

  const toDetails = useCallback((item, e, isJump = true) => {
    if (!item?.project_id) return '';
    const filter = encodeURIComponent(
      JSON.stringify({
        project: item?.project,
        projectId: item?.project_id,
        menuFrom: MENU_ID_MAP['pipeline_all-projects'], // 用于标识左侧菜单栏当前选中高亮
      }),
    );
    const url = SLASH_G_POP + `/Pipeline/ProjectList/Detail?filter=${filter}`;
    if (isJump) {
      e?.preventDefault();
      e?.stopPropagation();
      history.push(url);
    }

    return url;
  }, []);

  const pageSizeOptions = [10, 50, 100];
  const showPageableOperation = paginationMsg?.total > Math.min(...pageSizeOptions);
  // 表格使用到的插件
  const addons = [
    // scrollable({
    //   maxHeight: 500,
    // }),
    pageable({
      stateTextVisible: true,
      pageSizeVisible: showPageableOperation,
      pageIndexVisible: showPageableOperation,
      jumpVisible: showPageableOperation,
      endJumpVisible: showPageableOperation,
      pageIndex: paginationMsg?.pageIndex,
      pageSize: paginationMsg?.pageSize,
      pageSizeOptions: [10, 50, 100],
      recordCount: paginationMsg?.total,
      stateText: `${getNumberOfDotUnit(paginationMsg?.total)} item${
        paginationMsg?.total > 1 ? 's' : ''
      } in total`,
      onPagingChange: ({ pageIndex, pageSize }) => {
        setPaginationMsg({
          ...paginationMsg,
          pageIndex: pageIndex as number,
          pageSize: pageSize as number,
        });
        resetScroll();
      },
    }),
    sortable({
      columns: [
        'genre',
        'priority',
        'launch_date',
        'status',
        'next_checkpoint',
        'y1_revenue',
        'y3_revenue',
        'update_time',
      ],
      value: sorts ? [sorts] : [],
      onChange: (value) => {
        setIsClickSort(true);
        setSorts(value?.[0]);
        setSortsParams?.(value?.[0]);
        setPaginationMsg({ ...paginationMsg, pageIndex: 1 });
      },
    }),
    autotip({
      isLoading: loading,
      hideIcon: !loading, // loading状态显示图标，其他情况不显示
      emptyText: isNoPermission
        ? 'No permissions.'
        : 'The list has no data yet.',
      style: {
        height: 172,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
      },
    }),
  ];

  // if (paginationMsg?.total <= 20) addons.shift();

  useEffect(() => {
    if (!data || !Array.isArray(data?.items) || !data?.items?.length) {
      setRecords([]);
      return;
    }
    const ppsViewable = data?.items?.some(
      (item: any) => item?.pps_comment?.view_pps,
    );
    const updatesViewable = data?.items?.some(
      (item: any) => item?.updates_info?.view_update,
    );
    setRecords(data?.items);
    setPaginationMsg(
      Object.assign(paginationMsg, { total: data?.items?.length || 0 }),
    );
    setPPSViewable(ppsViewable);
    setUpdatesViewable(updatesViewable);
    setSalesViewable(!!data?.view_sales);

    if (!loading) {
      setTimeout(() => {
        const tableBody = document.querySelector(
          '.dbg-studio-overall-details-projects-dev-overview-table .tea-table__body',
        );
        // @ts-ignore
        const flag =
          tableBody?.clientHeight < tableBody?.firstChild?.clientHeight;
        setTableIsScroll(flag);
      });
    }
  }, [loading]);

  return (
    <>
      <Table
        className={classNames({
          'dbg-common-table dbg-studio-overall-details-projects-dev-overview-table sort-icon-color dbg-table-sticky dbh-action-state-center-table':
            true,
          'scroll-table-header': tableIsScroll,
        })}
        // verticalTop
        records={loading || isNoPermission ? [] : records}
        recordKey={(record) => record.id}
        columns={
          getColumns(
            toDetails,
            setProjectUpdatesVisible,
            setProjectUpdatesModalList,
            ppsOpenModal,
            ppsViewable,
            updatesViewable,
            salesViewable,
          ) as TableProps['columns']
        }
        addons={addons}
      />

      {/* Project Updates弹窗 */}
      <GeneralModal
        className="gp-studio-overall-details-projects-dev-overview-project-updates-modal globalpedia-custom-scrollbar gp-inital-list-style dbg-project-updates-form-modal"
        visible={projectUpdatesVisible}
        title="Project Updates"
        isFont14
        modalSize="xl"
        content={
          // @ts-ignore
          <ProjectUpdateCard
            type="default"
            itemList={projectUpdatesModalListNew}
            userIsAbleEdit={false}
          />
        }
        isFooter={false}
        onClose={() => setProjectUpdatesVisible(false)}
      />

      {/* PSS Comment弹窗 */}
      <GeneralModal
        className="gp-all-projects-list-pps-comment-modal globalpedia-custom-scrollbar gp-inital-list-style"
        visible={ppsModalVisible}
        title={ppsModalTitle}
        isFont14
        modalSize="l"
        content={
          <PPSCommentDetails
            data={ppsModalData}
            // text={ppsModalData?.comment}
            // updator={ppsModalData?.update_user}
            // updateTime={ppsModalData?.pps_update_time}
          />
        }
        footerButtonGroup={['close']}
        onClose={ppsCloseModal}
      />
    </>
  );
}

OverviewTable.defaultProps = DEFAULT_PROPS;

export default memo(OverviewTable);
